import type {
    OverviewBundlePartialResponse,
    OverviewBundleResponse,
    OverviewBundleSection,
    OverviewBundleSectionsOnlyResponse,
    DomainType,
} from '../../types/reporting';
import type { WidgetConfig } from '../../pages/reporting/widgets/types';
import { supportsMarketplaceBreakdown } from '../../pages/reporting/widgets/types';

/** Overview bundle sections (summary / timeseries / rankings). */
export const OVERVIEW_BUNDLE_DEFAULT_SECTIONS: readonly OverviewBundleSection[] = [
    'summary',
    'timeseries',
    'rankings',
];

/** Allowed section names for the overview bundle API. */
export const OVERVIEW_BUNDLE_SECTIONS: readonly OverviewBundleSection[] = [
    ...OVERVIEW_BUNDLE_DEFAULT_SECTIONS,
];

/** Per-domain /domain/stats fetch for overview marketplace / all-rows widgets. */
export interface WidgetDomainEndpointNeeds {
    domain: DomainType;
    includeBySource: boolean;
    allRows: boolean;
}

export interface WidgetDataNeeds {
    summary: boolean;
    timeseries: boolean;
    rankings: boolean;
    domain?: DomainType;
    /** Dedicated per-domain stats call (bySource and/or uncapped rows). */
    domainEndpoint?: WidgetDomainEndpointNeeds;
}

const ENTITY_DOMAINS = new Set<DomainType>(['location', 'vendor', 'insurance', 'oem']);

function isEntityRankingsWidget(config: WidgetConfig): boolean {
    return ENTITY_DOMAINS.has(config.domain as DomainType)
        && config.dataSource === 'rankings'
        && (
            config.widgetType === 'table'
            || config.widgetType === 'timeSeries'
            || config.widgetType === 'topList'
        );
}

/** Same rules as useWidgetData — which data source a widget reads. */
export function getWidgetDataNeeds(config: WidgetConfig): WidgetDataNeeds {
    if (config.widgetType === 'keyMetric') {
        return { summary: true, timeseries: false, rankings: false };
    }

    if (config.widgetType === 'timeSeries' && config.dataSource === 'timeseries') {
        return { summary: false, timeseries: true, rankings: false };
    }

    if (isEntityRankingsWidget(config)) {
        const domain = config.domain as DomainType;
        const includeBySource = Boolean(config.showMarketplaceBreakdown)
            && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric);
        const allRows = Boolean(config.showAllRows);

        // Marketplace / all-rows use POST /domain/stats, not the overview bundle.
        if (includeBySource || allRows) {
            return {
                summary: false,
                timeseries: false,
                rankings: false,
                domain,
                domainEndpoint: { domain, includeBySource, allRows },
            };
        }
    }

    if (config.dataSource === 'rankings') {
        return { summary: false, timeseries: false, rankings: true };
    }

    return { summary: false, timeseries: false, rankings: true };
}

export function getWidgetBundleSection(
    config: WidgetConfig,
    needs: WidgetDataNeeds,
): OverviewBundleSection | null {
    if (needs.summary) return 'summary';
    if (needs.timeseries) return 'timeseries';
    if (needs.rankings) return 'rankings';
    // domainEndpoint widgets do not use the overview bundle.
    return null;
}

function collectSectionsForWidgets(
    widgets: WidgetConfig[],
    predicate: (widget: WidgetConfig) => boolean,
): OverviewBundleSection[] {
    const sections = new Set<OverviewBundleSection>();
    for (const widget of widgets) {
        if (!predicate(widget)) continue;
        const needs = getWidgetDataNeeds(widget);
        const section = getWidgetBundleSection(widget, needs);
        if (section) sections.add(section);
    }
    return OVERVIEW_BUNDLE_SECTIONS.filter(section => sections.has(section));
}

/** Bundle sections needed from the `all` slice (widgets with Exclude EVs off). */
export function collectAllSections(widgets: WidgetConfig[]): OverviewBundleSection[] {
    return collectSectionsForWidgets(widgets, (widget) => !widget.excludeEvs);
}

/** Bundle sections that need the lazy excludeEvs slice for the current widget set. */
export function collectExcludeEvsSections(widgets: WidgetConfig[]): OverviewBundleSection[] {
    return collectSectionsForWidgets(widgets, (widget) => Boolean(widget.excludeEvs));
}

function widgetNeedsTimeseriesBySource(config: WidgetConfig): boolean {
    return config.widgetType === 'timeSeries'
        && config.dataSource === 'timeseries'
        && Boolean(config.showMarketplaceBreakdown)
        && supportsMarketplaceBreakdown(config.domain, config.widgetType, config.metric);
}

/** Union: any matching timeseries widget wants marketplace → shared timeseries includes bySource. */
export function collectTimeseriesIncludeBySource(
    widgets: WidgetConfig[],
    predicate: (widget: WidgetConfig) => boolean,
): boolean {
    return widgets.some(widget => predicate(widget) && widgetNeedsTimeseriesBySource(widget));
}

export function collectAllTimeseriesIncludeBySource(widgets: WidgetConfig[]): boolean {
    return collectTimeseriesIncludeBySource(widgets, (widget) => !widget.excludeEvs);
}

export function collectExcludeEvsTimeseriesIncludeBySource(widgets: WidgetConfig[]): boolean {
    return collectTimeseriesIncludeBySource(widgets, (widget) => Boolean(widget.excludeEvs));
}

/** True when cached timeseries is present but lacks marketplace bySource payloads. */
export function timeseriesLacksBySource(
    timeseries: OverviewBundleResponse['timeseries'] | undefined,
): boolean {
    const points = timeseries?.data;
    if (!points?.length) return true;
    return !points.some(
        point => point.bySource && Object.keys(point.bySource).length > 0,
    );
}

export function resolveBundleSection(
    base: OverviewBundleResponse | undefined,
    overlay: OverviewBundlePartialResponse | undefined,
    section: OverviewBundleSection,
    widgetExcludeEvs: boolean,
) {
    if (widgetExcludeEvs && overlay?.[section] !== undefined) {
        return overlay[section];
    }
    // Fall back to base (`all`) while the EV overlay section loads — callers must
    // treat this as stale via getBundleSectionLoadState (fade + spinner).
    return base?.[section];
}

/**
 * True when overlay already includes every requested excludeEvs section.
 * Used for completeness checks only — do not use alone to reuse overlays across
 * date ranges (query keys must match).
 */
export function overlayCoversSections(
    overlay: OverviewBundlePartialResponse | undefined,
    sections: readonly OverviewBundleSection[],
): boolean {
    if (!overlay || sections.length === 0) return false;
    return sections.every(section => overlay[section] !== undefined);
}

/** Sections requested but not yet present on a merged section cache. */
export function missingBundleSections(
    cache: Pick<OverviewBundleResponse, OverviewBundleSection> | undefined,
    requested: readonly OverviewBundleSection[],
): OverviewBundleSection[] {
    return requested.filter(section => cache?.[section] === undefined);
}

/** @deprecated Prefer missingBundleSections — same behavior for EV overlay. */
export function missingExcludeEvsSections(
    overlay: OverviewBundlePartialResponse | undefined,
    requested: readonly OverviewBundleSection[],
): OverviewBundleSection[] {
    return missingBundleSections(overlay, requested);
}

/**
 * Merge a selective `all`-slice delta into the range-keyed base cache.
 * Existing sections are preserved so growing the section set does not drop them.
 */
export function mergeBaseSections(
    prev: OverviewBundleResponse | undefined,
    delta: OverviewBundleResponse | OverviewBundleSectionsOnlyResponse,
    requestedSections: readonly OverviewBundleSection[],
): OverviewBundleResponse {
    return {
        source: 'rollup',
        sectionsOnly: true,
        sections: [...requestedSections],
        summary: delta.summary ?? prev?.summary,
        timeseries: delta.timeseries ?? prev?.timeseries,
        rankings: delta.rankings ?? prev?.rankings,
    };
}

/**
 * Merge a partial EV overlay delta into the range-keyed merged cache.
 * Existing sections are preserved so growing the section set does not drop them.
 */
export function mergeExcludeEvsOverlay(
    prev: OverviewBundlePartialResponse | undefined,
    delta: OverviewBundlePartialResponse,
    requestedSections: readonly OverviewBundleSection[],
): OverviewBundlePartialResponse {
    return {
        source: 'rollup',
        excludeEvsSectionsOnly: true,
        excludeEvsSections: [...requestedSections],
        summary: delta.summary ?? prev?.summary,
        timeseries: delta.timeseries ?? prev?.timeseries,
        rankings: delta.rankings ?? prev?.rankings,
    };
}

/**
 * Keep last-settled params paired with placeholder (cross-range) bundle data.
 * Updates the ref only when the query is fresh so a range switch cannot reformat
 * the previous buckets with the new granularity/window.
 */
export function latchSettledDisplayParams<T>(
    settledRef: { current: T },
    live: T,
    isPlaceholderData: boolean,
): T {
    if (!isPlaceholderData) {
        settledRef.current = live;
    }
    return isPlaceholderData ? settledRef.current : live;
}

/**
 * Pick display params for the rows on screen. Stale overlay / base snapshots win
 * over live page params so charts never mix old points with new formatting.
 */
export function resolveBundleDisplayParams<T>(options: {
    liveParams: T;
    baseIsStale: boolean;
    baseDisplayParams: T;
    preferStaleOverlay: boolean;
    overlayDisplayParams: T;
}): T {
    if (options.preferStaleOverlay) return options.overlayDisplayParams;
    if (options.baseIsStale) return options.baseDisplayParams;
    return options.liveParams;
}

/**
 * Initial load → skeleton; stale `all` while EV overlay loads / refetch with data →
 * fade + header spinner.
 */
export function getBundleSectionLoadState(options: {
    sectionFetching: boolean;
    hasBaseData: boolean;
    widgetExcludeEvs: boolean;
    needsOverlay: boolean;
    overlayHasSection: boolean;
}): { isInitialLoading: boolean; isRefetching: boolean } {
    const {
        sectionFetching,
        hasBaseData,
        widgetExcludeEvs,
        needsOverlay,
        overlayHasSection,
    } = options;

    /** Exclude EVs on, base visible, EV section not ready yet — keep `all` but fade it. */
    const waitingForOverlay =
        widgetExcludeEvs
        && needsOverlay
        && hasBaseData
        && !overlayHasSection;

    const hasUsableEvData = widgetExcludeEvs && needsOverlay && overlayHasSection;

    const isInitialLoading = !hasBaseData && (
        sectionFetching
        || (widgetExcludeEvs && needsOverlay && !overlayHasSection)
    );

    const isRefetching = waitingForOverlay
        || (sectionFetching && (
            hasUsableEvData
            || (!widgetExcludeEvs && hasBaseData)
            || (widgetExcludeEvs && !needsOverlay && hasBaseData)
        ));

    return { isInitialLoading, isRefetching };
}
